/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { Flex } from '#UNSAFE_Flex';
import { IconButton } from '#UNSAFE_IconButton';
import { CaretDown } from '#UNSAFE_RedwoodIcons/CaretDown';
import { CaretUp } from '#UNSAFE_RedwoodIcons/CaretUp';
import { ChevronLeft } from '#UNSAFE_RedwoodIcons/ChevronLeft';
import { ChevronRight } from '#UNSAFE_RedwoodIcons/ChevronRight';
import { RadioButtonSmallOn } from '#UNSAFE_RedwoodIcons/RadioButtonSmallOn';
import { Text } from '#UNSAFE_Text';
import { ToggleButton } from '#UNSAFE_ToggleButton';
import { View } from '#UNSAFE_View';
import { TestIdProps } from '#hooks/UNSAFE_useTestId';
import { useUser } from '#hooks/UNSAFE_useUser';
import { getMonthName } from '#utils/PRIVATE_dayUtils';
import {
  CalendarMonthRequired,
  getLongFormattedYearMonth,
  getNumericFormattedYear
} from '#utils/UNSAFE_calendarDateUtils';
import { ComponentProps, useMemo } from 'preact/compat';
import { styles } from '#PRIVATE_DatePickerHeader/themes/DatePickerHeaderStyles.css';

type ToggleButtonOnToggle = NonNullable<ComponentProps<typeof ToggleButton>['onToggle']>;
type IconButtonOnAction = NonNullable<ComponentProps<typeof IconButton>['onAction']>;

type ToggleButtonsProps = {
  /**
   * Use this property to specify how to render the header part of the component.
   * <ul>
   *   <li>
   *     `'independentButtons'` - The component will show two toggle buttons,
   *     one for the month and one for the year.
   *   </li>
   *   <li>
   *     `'text'` - The component will show the month and year as text in the header.
   *   </li>
   * </ul>
   * @default 'independentButtons'
   */
  monthYearHeader?: 'independentButtons' | 'text';

  /**
   * Use this property to specify the selection state of the header toggle buttons. This property
   * is only applicable when the `monthYearHeader` is not set to `'text'`.
   * <ul>
   *   <li>
   *     `'month'` - The month toggle button is selected. Only applicable when the
   *     `monthYearHeader` is set to `'independentButtons'`.
   *   </li>
   *   <li>
   *     `'year'` - The year toggle button is selected. Only applicable when the
   *     `monthYearHeader` is set to `'independentButtons'`.
   *   </li>
   *   <li>
   *     `'none'` - No selection.
   *   </li>
   * </ul>
   * @default 'none'
   */
  toggleSelection?: 'month' | 'year' | 'none';

  /**
   * Accessible label for the month toggle button. Only applicable when
   * `monthYearHeader` is set to `'individualButtons'`.
   *
   * Use this property to provide a screen reader accessible label for the
   * month toggle button. For example, you might want the screen reader
   * to read the whole month "March", instead of what is shown on the
   * button - "Mar". Or you might want the screen reader to read additional
   * information like, "Current month is March, press this toggle button to
   * view a different month." Defaults to the full month name.
   */
  monthToggleAccessibleLabel?: string;

  /**
   * Accessible label for the year toggle button. Only applicable when
   * `monthYearHeader` is set to `'individualButtons'`.
   *
   * Use this property to provide a screen reader accessible label for the
   * month toggle button. For example, you might want the screen reader to
   * read additional information like, "Current year is 2024, press this toggle
   * button to view a different year."
   */
  yearToggleAccessibleLabel?: string;

  /**
   * A callback function to be called when the month toggle button is clicked.
   * Only applicable when `monthYearHeader` is set to `'independentButtons'`.
   */
  onMonthToggle?: ToggleButtonOnToggle;

  /**
   * A callback function to be called when the year toggle button is clicked.
   * Only applicable when `monthYearHeader` is set to `'independentButtons'`.
   */
  onYearToggle?: ToggleButtonOnToggle;
};

type Props = TestIdProps &
  ToggleButtonsProps & {
    /**
     * The number representing the Month. The valid values are 1-12.
     */
    month: CalendarMonthRequired['month'];

    /**
     * The number representing the Year in ISO format. A valid ISO year will be within the
     * range of 0 and 9999.
     */
    year: CalendarMonthRequired['year'];

    /**
     * Set this property to true to hide the previous navigation button.
     * @default false
     */
    isPreviousNavigationButtonHidden?: boolean;

    /**
     * Set this property to true to hide the today navigation button.
     * @default false
     */
    isTodayNavigationButtonHidden?: boolean;

    /**
     * Set this property to true to hide the next navigation button.
     * @default false
     */
    isNextNavigationButtonHidden?: boolean;

    /**
     * Accessible label for the previous navigation button. Only applicable when
     * `isPreviousNavigationButtonHidden` is set to `false`.
     *
     * Use this property to provide a screen reader accessible label for the
     * previous navigation button. For example, you might want the screen reader to
     * read additional information like, "Go to previous month."
     */
    previousNavigationAccessibleLabel: string;

    /**
     * Accessible label for the today navigation button. Only applicable when
     * `isTodayNavigationButtonHidden` is set to `false`.
     *
     * Use this property to provide a screen reader accessible label for the
     * today navigation button. For example, you might want the screen reader to
     * read additional information like, "Go to today."
     */
    todayNavigationAccessibleLabel: string;

    /**
     * Accessible label for the next navigation button. Only applicable when
     * `isNextNavigationButtonHidden` is set to `false`.
     *
     * Use this property to provide a screen reader accessible label for the
     * next navigation button. For example, you might want the screen reader to
     * read additional information like, "Go to next month."
     */
    nextNavigationAccessibleLabel: string;

    /**
     * A callback function to be called when the previous navigation button is
     * clicked. Only applicable when `isPreviousNavigationButtonHidden` is set to `false`.
     */
    onPreviousNavigationAction?: IconButtonOnAction;

    /**
     * A callback function to be called when the today button is clicked. Only
     * applicable when `isTodayNavigationButtonHidden` is set to `false`.
     */
    onTodayNavigationAction?: IconButtonOnAction;

    /**
     * A callback function to be called when the next navigation button is
     * clicked. Only applicable when `isNextNavigationButtonHidden` is set to `false`.
     */
    onNextNavigationAction?: IconButtonOnAction;
  };

/**
 * The DatePickerHeader component can be used to render the header for the
 * DatePicker component. It shows the current month, year and some additional
 * elements based on the configuration like previous, today and next navigation
 * buttons.
 */
export const DatePickerHeader = ({
  isPreviousNavigationButtonHidden = false,
  isTodayNavigationButtonHidden = false,
  isNextNavigationButtonHidden = false,
  month,
  monthToggleAccessibleLabel,
  monthYearHeader = 'independentButtons',
  nextNavigationAccessibleLabel,
  previousNavigationAccessibleLabel,
  testId,
  todayNavigationAccessibleLabel,
  toggleSelection = 'none',
  year,
  yearToggleAccessibleLabel,
  onMonthToggle,
  onNextNavigationAction,
  onPreviousNavigationAction,
  onTodayNavigationAction,
  onYearToggle
}: Props) => {
  const { locale, direction } = useUser();

  // format month and year to long string
  // { month: 3, year 2024 } -> 'March 2024'
  const longFormattedYearMonth = useMemo(
    () => getLongFormattedYearMonth(locale, year, month),
    [locale, month, year]
  );

  // format month and year to strings
  // { month: 3, year 2024 } -> { monthStr: 'Mar', yearStr: '2024' }
  const { monthStr, yearStr, monthLongStr } = useMemo(
    () => ({
      monthStr: getMonthName(locale, month, year, 'short'),
      yearStr: getNumericFormattedYear(locale, year, month),
      monthLongStr: getMonthName(locale, month, year, 'long')
    }),
    [locale, month, year]
  );

  return (
    <Flex align="center" justify="between" testId={testId}>
      <Flex>
        {monthYearHeader === 'independentButtons' && (
          <>
            <div class={styles['monthYearButtonContainer']}>
              <ToggleButton
                aria-label={monthToggleAccessibleLabel ?? monthLongStr}
                label={monthStr}
                endIcon={toggleSelection === 'month' ? <CaretUp /> : <CaretDown />}
                isSelected={toggleSelection === 'month'}
                onToggle={onMonthToggle}
                size="sm"
                variant="borderless"
              />
              <ToggleButton
                aria-label={yearToggleAccessibleLabel}
                label={yearStr}
                endIcon={toggleSelection === 'year' ? <CaretUp /> : <CaretDown />}
                isSelected={toggleSelection === 'year'}
                onToggle={onYearToggle}
                size="sm"
                variant="borderless"
              />
            </div>
          </>
        )}
        {monthYearHeader === 'text' && (
          <View paddingInlineStart="3x">
            <Text size="md" weight="semiBold">
              {longFormattedYearMonth}
            </Text>
          </View>
        )}
      </Flex>
      <Flex>
        {!isPreviousNavigationButtonHidden && (
          <IconButton
            aria-label={previousNavigationAccessibleLabel}
            size="sm"
            variant="borderless"
            onAction={onPreviousNavigationAction}>
            {direction === 'ltr' ? <ChevronLeft /> : <ChevronRight />}
          </IconButton>
        )}
        {!isTodayNavigationButtonHidden && (
          <IconButton
            aria-label={todayNavigationAccessibleLabel}
            size="sm"
            variant="borderless"
            onAction={onTodayNavigationAction}>
            <RadioButtonSmallOn />
          </IconButton>
        )}
        {!isNextNavigationButtonHidden && (
          <IconButton
            aria-label={nextNavigationAccessibleLabel}
            size="sm"
            variant="borderless"
            onAction={onNextNavigationAction}>
            {direction === 'ltr' ? <ChevronRight /> : <ChevronLeft />}
          </IconButton>
        )}
      </Flex>
    </Flex>
  );
};
